/**
 * Theme provider with a persisted light/dark/system preference.
 *
 * Builds the MUI theme (and the mode-aware design tokens) from the resolved
 * mode; "system" tracks prefers-color-scheme live via useMediaQuery.
 * CssBaseline paints <body> with the palette background and, with
 * enableColorScheme, stamps the CSS color-scheme on <html> so native UI
 * (scrollbars, form controls) follows too.
 */
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { CssBaseline, ThemeProvider, useMediaQuery } from "@mui/material";
import { buildTheme } from "../theme";
import {
  ThemeModeContext,
  loadThemePref,
  saveThemePref,
  type ThemeModeValue,
  type ThemePref,
} from "../hooks/useThemeMode";

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(loadThemePref);
  const systemDark = useMediaQuery("(prefers-color-scheme: dark)");

  const mode = pref === "system" ? (systemDark ? "dark" : "light") : pref;
  const theme = useMemo(() => buildTheme(mode), [mode]);

  const setPref = useCallback((next: ThemePref) => {
    setPrefState(next);
    saveThemePref(next);
  }, []);

  const value = useMemo<ThemeModeValue>(() => ({ pref, setPref }), [pref, setPref]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}
